/**
 * Sun position and sky classification — pure astronomy, no API.
 *
 * Given where you are and what time it is, the sun's elevation tells you
 * whether it's daylight, golden hour, blue hour, or night. Cloud cover
 * (from the weather API) refines daylight into sun vs overcast.
 */

const toRad = (d: number) => (d * Math.PI) / 180
const toDeg = (r: number) => (r * 180) / Math.PI

/** Milliseconds at the J2000 epoch (2000-01-01 12:00 UTC). */
const J2000_MS = 946728000000

/**
 * Sun elevation above the horizon in degrees, using the standard low-
 * precision solar position algorithm (accurate to well under a degree —
 * plenty for telling golden hour from night).
 */
export function sunElevationDeg(date: Date, latDeg: number, lonDeg: number): number {
  const n = (date.getTime() - J2000_MS) / 86400000

  // Mean longitude and mean anomaly of the sun.
  const L = (280.46 + 0.9856474 * n) % 360
  const g = toRad((357.528 + 0.9856003 * n) % 360)

  // Ecliptic longitude and obliquity.
  const lambda = toRad(L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g))
  const epsilon = toRad(23.439 - 0.0000004 * n)

  // Declination and right ascension.
  const delta = Math.asin(Math.sin(epsilon) * Math.sin(lambda))
  const ra = Math.atan2(Math.cos(epsilon) * Math.sin(lambda), Math.cos(lambda))

  // Local hour angle via sidereal time.
  const gmstHours = (18.697374558 + 24.06570982441908 * n) % 24
  const lstDeg = (gmstHours * 15 + lonDeg) % 360
  const hourAngle = toRad(lstDeg) - ra

  const lat = toRad(latDeg)
  const sinAlt =
    Math.sin(lat) * Math.sin(delta) + Math.cos(lat) * Math.cos(delta) * Math.cos(hourAngle)
  return toDeg(Math.asin(sinAlt))
}

export interface Sky {
  id: 'sun' | 'overcast' | 'low-sun' | 'blue-hour' | 'night'
  /** What it looks like outside, for the suggestion chip. */
  blurb: string
  /** Scene brightness estimate as EV at ISO 100, outdoors. */
  ev: number
  /** True while the sun is up — drives indoor day/night suggestions. */
  isDaylight: boolean
}

/**
 * Sun elevation + cloud cover -> what kind of light it is outside.
 * Thresholds: civil twilight ends at -6°; below ~10° the light goes long
 * and warm (golden hour); heavy cloud costs about three stops of sun.
 */
export function classifySky(elevationDeg: number, cloudCoverPct: number): Sky {
  if (elevationDeg < -6) {
    return { id: 'night', blurb: 'night', ev: 4, isDaylight: false }
  }
  if (elevationDeg < 0) {
    return { id: 'blue-hour', blurb: 'dusk — blue hour', ev: 6, isDaylight: false }
  }
  if (elevationDeg < 10) {
    return { id: 'low-sun', blurb: 'low sun — golden hour', ev: 9, isDaylight: true }
  }
  if (cloudCoverPct >= 40) {
    return { id: 'overcast', blurb: `cloudy (${Math.round(cloudCoverPct)}% cover)`, ev: 12, isDaylight: true }
  }
  return { id: 'sun', blurb: 'clear and bright', ev: 15, isDaylight: true }
}
